import "./EmployeeShortProfile.css";
import { MdLocationOn,  MdOutlineCloudUpload} from "react-icons/md";
import { useEffect, useRef, useState } from "react";
import useProfileImage from "../../hooks/useProfileImage.js";
import { Toast } from "primereact/toast";
import DefaultImage from "../../medias/images/default.jpg"
import { updateUserPhotoRequest } from "../../services/wisdomServices/userService.js";
import { getSessionItem } from "../../utils/helper/session.js";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { FileUpload } from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Tag } from "primereact/tag";
import { confirmDialog } from 'primereact/confirmdialog';
import { IdvStatus } from "../idvStatus/IdvStatus.jsx";

/**
 * @function EmployeeShortProfile
 * @param {Object} props 
 */
function EmployeeShortProfile(props) {
  const toast = useRef(null)
  const userImage = useProfileImage()
  const [imageBase64, setImageBase64] = useState(null)
  const [profileImage, setProfileImage] = useState(userImage);
  const [userId, setUserId] = useState()
  const [user, setUser] = useState()
  const [loaded, setLoaded] = useState(false)
  const [showIcon, setShowIcon] = useState(false);
  const [visible, setVisible] = useState(false)
  const [totalSize, setTotalSize] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileUploadRef = useRef(null);

  const confirmResetImage = () => {
    confirmDialog({
      message: 'Your current profile picture will be permanently removed and cannot be recovered. Continue?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: 'Yes, remove',
      rejectLabel: 'Cancel',
      accept: handleResetImage
    });
  };

  useEffect(() => {
    let user = getSessionItem("User")
    setUser(user)
    setUserId(user.id)
    setLoaded(true)
  }, [])

   const showWarning = (text) => {
    toast.current.show({severity:'warning', summary: 'Warning', detail:text, life: 3000});
  }

  const onTemplateSelect = async(e) => {
    const loadedFile = e.files[0];
    if (!loadedFile || (loadedFile.type !== 'image/jpeg' && loadedFile.type !== 'image/png')) {
      showWarning('Please, select an JPEG or PNG file.');

      fileUploadRef.current.clear();

      setImageBase64(null);

      return;
    }

    let _totalSize = totalSize;
    _totalSize += loadedFile.size || 0;
    setTotalSize(_totalSize);

    if (loadedFile) {
      try {
        const base64 = await convertToBase64(loadedFile);
        setImageBase64(base64);
      } catch (err) {
        console.error(err);
        showWarning("Error processing image");
      }
    }
  };

  const handleResetImage = async () => {
    try {
      setDeleting(true)
      const payload = {
        imageBase64: ""
      };

      await updateUserPhotoRequest(userId, payload);

      setProfileImage(DefaultImage);

      setImageBase64(null);
      fileUploadRef.current?.clear();

      sessionStorage.removeItem("profileImage");

      window.dispatchEvent(new Event("profileImageUpdated"));


    } catch (error) {
      console.error(error);
      showWarning("Failed to reset image");
    } finally {
      setDeleting(false)
    }
  };

  const onTemplateUpload = async(e) => {
    if (!imageBase64) {
      showWarning("Please select an image first");
      return;
    }

    try {
      setUploading(true)
      const payload = {
        imageBase64: imageBase64
      };
      await updateUserPhotoRequest(userId, payload);
      setProfileImage(imageBase64);
      sessionStorage.setItem("profileImage", imageBase64);
      window.dispatchEvent(new Event("profileImageUpdated"));
      setVisible(false);

    } catch (error) {
      console.error(error);
      showWarning("Upload failed");
    } finally {
      setUploading(false)
    }
  };

  const onTemplateRemove = (file, callback) => {
      setTotalSize(totalSize - file.size);
      callback();
  };

  const onTemplateClear = () => {
      setTotalSize(0);
  };

  const headerTemplate = (options) => {
    const { className, chooseButton, uploadButton, cancelButton } = options;
    const value = totalSize / 10000;
    const formatedValue = fileUploadRef && fileUploadRef.current ? fileUploadRef.current.formatSize(totalSize) : '0 B';

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center', justifyContent:"space-between" }}>
        <div>
          {chooseButton}
          <Button
            icon="pi pi-cloud-upload"
            className="p-button-success p-button-rounded p-button-outlined"
            onClick={onTemplateUpload}
            loading={uploading}
            disabled={!imageBase64 || uploading || deleting}
          />
          <Button
            title="Remove your current photo and set the default in place"
            icon="pi pi-trash"
            className="p-button-danger p-button-outlined"
            onClick={confirmResetImage}
            loading={deleting}
            disabled={!profileImage || profileImage === DefaultImage}
          />
        </div>
        <div className="flex align-items-center gap-3 ml-auto">
          <span>{formatedValue} / 1 MB</span>
          <ProgressBar value={value} showValue={false} style={{ width: '10rem', height: '12px' }}></ProgressBar>
        </div>
      </div>
    );
  };

  const itemTemplate = (file, props) => {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div className="flex align-items-center" style={{ display:"flex", flex:"1", alignItems:"center", gap:"8px" }}>
          <img 
            alt={file.name} 
            role="presentation" 
            src={file.objectURL} 
            style={{ 
              width: 60,
              height: 60,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <span style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            {file.name}
            <small>{new Date().toLocaleDateString()}</small>
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
          <Tag value={props.formatSize} severity="warning" style={{ padding:"8px" }}/>
          <Button type="button" icon="pi pi-times" className="p-button-outlined p-button-rounded p-button-danger ml-auto" onClick={() => onTemplateRemove(file, props.onRemove)} />
        </div>
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex align-items-center flex-column">
        <i className="pi pi-image mt-3 p-5" style={{ fontSize: '5em', borderRadius: '50%', backgroundColor: 'var(--surface-b)', color: 'var(--surface-d)' }}></i>
        <span style={{ fontSize: '1.2em', color: 'var(--text-color-secondary)' }} className="my-5">
            Drag and Drop Image Here
        </span>
      </div>
    );
  };
  
  const chooseOptions = { icon: 'pi pi-fw pi-images', iconOnly: true, className: 'custom-choose-btn p-button-rounded p-button-outlined cursor-pointer' };
  const uploadOptions = { icon: 'pi pi-fw pi-cloud-upload', iconOnly: true, className: 'custom-upload-btn p-button-success p-button-rounded p-button-outlined cursor-pointer' };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        const result = reader.result;
        const base64 = result.split(',')[1];
        resolve(base64);
      };

      reader.onerror = reject;
    });
  };

  useEffect(() => {
    setProfileImage(userImage);
  }, [userImage]);

  return (
    <>
      <Toast ref={toast} position="top-center"/>
      {loaded && (
        <div className="EmployeeShortProfile">
          <div className="image-box" onMouseEnter={() => setShowIcon(true)} onMouseLeave={() => setShowIcon(false)}>
            <img className="summaryProfileImage" src={profileImage} alt="user profile" />
            {/* Check if the showIcon = true and loggedUser !==user in profile page */}
            {showIcon && user?.id == props.employeeID &&(
              <div className="icone-cloud-upload">
                <MdOutlineCloudUpload onClick={() => setVisible(true)}/>
              </div>
            )}
          </div>
          <div className="summaryProfileInformation">
            <div className="user-view-short-profile">
              <p className="short-profile-user-name">{props.employeeName}</p>
              <p className="short-profile-user-pronoun">{' ('+props.employeePronoun+')'}</p>
              <IdvStatus status={props?.employeeIdvStatus} />
            </div>
            
            <span className="summaryProfile-designation">
            {props.employeeDepartment} - {props.employeeDesignation}
            </span>
            <span className="summaryProfile-ID">
              Employee ID: {props.employeeID}
            </span>
            <span className="summaryProfile-status">{props.employeeUsername}</span>
            <span className="summaryProfile-location">
              <MdLocationOn />
              {props.employeeLocation}
            </span>
          </div>
        </div>
      )}
      <Dialog
        visible={visible}
        onHide={() => setVisible(false)}
        header="Upload Profile Picture"
        style={{width:"1000px"}}
      >
        <div style={{width:"100%"}}>
          <Tooltip target=".custom-choose-btn" content="Choose" position="bottom" />
          <Tooltip target=".custom-upload-btn" content="Upload" position="bottom" />

          <FileUpload 
            ref={fileUploadRef}
            name="profile-picture-upload"
            customUpload
            uploadHandler={onTemplateUpload}
            multiple={false}
            accept="image/*"
            maxFileSize={1024 * 1024}
            onSelect={onTemplateSelect}
            onError={onTemplateClear}
            onClear={onTemplateClear}
            headerTemplate={headerTemplate}
            itemTemplate={itemTemplate}
            emptyTemplate={emptyTemplate}
            chooseOptions={chooseOptions}
            uploadOptions={uploadOptions}
          />
        </div>
      </Dialog>
    </>
  );
}

export default EmployeeShortProfile;
